import { EnterpriseProfileType } from "@mg/schemas/src/commons";

/**
 * The shape of the redux auth user we depend on for the onboarding
 * redirect guard. Kept narrow on purpose so the predicate is trivially
 * testable without dragging in the full User schema.
 */
export interface OnboardingGuardUser {
  isEnterpriseOnboarded?: boolean;
  referredBy?: string | null;
  role?: EnterpriseProfileType | null;
}

/**
 * Decide whether a freshly-arrived auth-layout visitor should be punted
 * to `/add-password`.
 *
 * Returns true ONLY when:
 *   - the user has not yet completed enterprise onboarding, AND
 *   - they were NOT brought in via the existing creative-network referral
 *     (`referredBy`), AND
 *   - they have NOT explicitly started the onboarding flow already
 *     (localStorage flag set by /onboarding entry), AND
 *   - they are NOT a share-link invitee (CATALYST_REQUESTER role, marked
 *     INVITED until they convert via the emailed invite link).
 *
 * The share-link bypass is the load-bearing piece for the email-only
 * gate. A share-link visitor who's just entered their email gets a
 * CATALYST_REQUESTER+INVITED session so they can immediately see their
 * allow-listed ticket. If we forced /add-password on them here they'd
 * be unable to read the ticket they just unlocked, defeating the entire
 * "see ticket immediately, complete signup later" flow.
 *
 * Pure function so it's safe to unit test without the router/store.
 */
export function shouldRedirectToAddPassword(
  user: OnboardingGuardUser,
  hasStartedOnboardingFlow: boolean,
): boolean {
  if (user.isEnterpriseOnboarded) return false;
  if (user.referredBy) return false;
  if (hasStartedOnboardingFlow) return false;
  if (user.role === EnterpriseProfileType.CATALYST_REQUESTER) return false;
  return true;
}
